var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '@/styles/Rooms/RoomsPage.less';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApiInstance } from '@/api';
import BoardListItem from '@/components/Board/BoardListItem';
import LabeledActionForm from '@/components/UI/LabeledActionForm';
import Loader from '@/components/UI/Loader';
import NotificationPopup from '@/components/UI/NotificationPopup';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { useNotification } from '@/hooks/useNotification';
export default function BoardsList() {
    const [boardCode, setBoardCode] = useState('');
    const [createBoardTitle, setCreateBoardTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [boards, setBoards] = useState([]);
    const [boardsLoading, setBoardsLoading] = useState(false);
    const [tooltip, setTooltip] = useState({ boardCode: null, type: null });
    const [editingBoardCode, setEditingBoardCode] = useState(null);
    const [editBoardTitle, setEditBoardTitle] = useState('');
    const { useMockApi } = useApiContext();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const api = useApiInstance();
    const boardApi = api.boardApiInstance;
    // notifications
    const { notificationList, showNotification, showError } = useNotification(2000);
    // Подключение к доске по коду
    const handleJoin = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        if (!boardCode.trim()) {
            showError(t('boards.enterBoardCode'));
            return;
        }
        setLoading(true);
        try {
            const board = yield boardApi.getBoardByCode(boardCode.trim());
            if (board) {
                navigate(`/boards/${board.code}`);
            }
            else {
                showError(t('boards.boardNotFound'));
            }
        }
        catch (err) {
            showError(t('boards.boardNotFound'));
        }
        finally {
            setLoading(false);
        }
    });
    // Создание доски
    const handleCreate = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        if (!createBoardTitle.trim()) {
            showError(t('boards.enterBoardName'));
            return;
        }
        setLoading(true);
        try {
            const board = yield boardApi.create(createBoardTitle.trim());
            if (board && board.code) {
                setBoards((prev) => [board, ...prev]);
                setCreateBoardTitle('');
                showNotification(t('boards.notifications.boardCreated') || 'Created');
            }
            else {
                showError(t('errors.error'));
            }
        }
        catch (err) {
            showError(t('errors.error'));
        }
        finally {
            setLoading(false);
        }
    });
    useEffect(() => {
        if (!user)
            return;
        if (useMockApi)
            return;
        setBoardsLoading(true);
        boardApi
            .list(user.id)
            .then((res) => setBoards(res.results || res))
            .catch(() => showError(t('errors.error')))
            .finally(() => setBoardsLoading(false));
    }, [user, useMockApi, showError, t, boardApi]);
    const handleDeleteBoard = (code) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield boardApi.delete(code);
            setBoards((prev) => prev.filter((b) => b.code !== code));
        }
        catch (_a) {
            showError(t('errors.error'));
        }
    });
    const handleEditBoardSave = (board) => __awaiter(this, void 0, void 0, function* () {
        try {
            if (editBoardTitle.trim() !== (board.title || '')) {
                const updated = yield boardApi.update(board.code, { title: editBoardTitle.trim() });
                setBoards((prev) => prev.map((b) => (b.code === board.code ? Object.assign(Object.assign({}, b), { title: updated.title }) : b)));
            }
        }
        catch (_a) {
            showError(t('errors.error'));
        }
        finally {
            setEditingBoardCode(null);
            setTooltip({ boardCode: null, type: null });
        }
    });
    const handleEditStart = (board) => {
        setEditingBoardCode(board.code);
        setEditBoardTitle(board.title || '');
    };
    const handleEditCancel = () => {
        setEditingBoardCode(null);
        setEditBoardTitle('');
        setTooltip({ boardCode: null, type: null });
    };
    return (_jsx("div", { className: "rooms-page", children: _jsxs("div", { className: "wrapper", children: [_jsx(NotificationPopup, { notificationList: notificationList }), _jsx("h2", { children: t('boards.title', 'Доски') }), _jsx("div", { className: "join-room-form", children: _jsx(LabeledActionForm, { label: t('boards.boardCode'), value: boardCode, onChange: setBoardCode, buttonText: t('boards.join'), onSubmit: handleJoin, disabled: loading, autoComplete: "off" }) }), user && (_jsxs(_Fragment, { children: [_jsx("div", { className: "create-room-form", children: _jsx(LabeledActionForm, { label: t('boards.boardName'), value: createBoardTitle, onChange: setCreateBoardTitle, buttonText: t('boards.createBoard'), onSubmit: handleCreate, disabled: loading, autoComplete: "off" }) }), _jsxs("div", { className: "rooms-list-container", children: [_jsx("h3", { children: t('boards.myBoards', 'My boards') }), boardsLoading ? (_jsx(Loader, {})) : boards.length === 0 ? (_jsx("div", { children: t('boards.noBoards', 'No boards') })) : (_jsx("ul", { className: "user-rooms-list", children: boards.map((board) => (_jsx(BoardListItem, { board: board, isEditing: editingBoardCode === board.code, editTitle: editBoardTitle, onEditTitleChange: setEditBoardTitle, onEditStart: handleEditStart, onEditSave: handleEditBoardSave, onEditCancel: handleEditCancel, onDelete: handleDeleteBoard, tooltip: tooltip, onTooltipChange: setTooltip }, board.code))) }))] })] }))] }) }));
}
